import { openDB, DBSchema, IDBPDatabase } from 'idb';

// Define the database schema
interface OfflineDB extends DBSchema {
  watchlists: {
    key: string;
    value: {
      id: string;
      data: any;
      timestamp: number;
    };
    indexes: { 'by-timestamp': number };
  };
  movies: {
    key: string;
    value: {
      id: string;
      data: any;
      timestamp: number;
    };
    indexes: { 'by-timestamp': number };
  };
  userMovieData: {
    key: string;
    value: {
      id: string;
      data: any;
      timestamp: number;
    };
    indexes: { 'by-timestamp': number };
  };
  pendingActions: {
    key: string;
    value: {
      id: string;
      type: 'add' | 'update' | 'delete';
      collection: string;
      data: any;
      timestamp: number;
      retryCount: number;
    };
    indexes: { 'by-timestamp': number };
  };
  appMetadata: {
    key: string;
    value: any;
  };
}

class OfflineStorageService {
  private db: IDBPDatabase<OfflineDB> | null = null;
  private dbName = 'watchlist-offline-db';
  private dbVersion = 1;

  async initialize(): Promise<void> {
    if (this.db) {
      return;
    }

    try {
      this.db = await openDB<OfflineDB>(this.dbName, this.dbVersion, {
        upgrade(db) {
          // Watchlists store
          if (!db.objectStoreNames.contains('watchlists')) {
            const watchlistStore = db.createObjectStore('watchlists', {
              keyPath: 'id',
            });
            watchlistStore.createIndex('by-timestamp', 'timestamp');
          }

          // Movies store
          if (!db.objectStoreNames.contains('movies')) {
            const movieStore = db.createObjectStore('movies', {
              keyPath: 'id',
            });
            movieStore.createIndex('by-timestamp', 'timestamp');
          }

          // User movie data store
          if (!db.objectStoreNames.contains('userMovieData')) {
            const userMovieDataStore = db.createObjectStore('userMovieData', {
              keyPath: 'id',
            });
            userMovieDataStore.createIndex('by-timestamp', 'timestamp');
          }

          // Pending actions queue (for offline writes)
          if (!db.objectStoreNames.contains('pendingActions')) {
            const pendingStore = db.createObjectStore('pendingActions', {
              keyPath: 'id',
            });
            pendingStore.createIndex('by-timestamp', 'timestamp');
          }

          // App metadata (last sync time, etc.)
          if (!db.objectStoreNames.contains('appMetadata')) {
            db.createObjectStore('appMetadata');
          }
        },
      });

      console.log('✅ OfflineStorage: IndexedDB initialized');
    } catch (error) {
      console.error('❌ OfflineStorage: Failed to initialize IndexedDB:', error);
      throw error;
    }
  }

  private ensureDB(): IDBPDatabase<OfflineDB> {
    if (!this.db) {
      throw new Error('Database not initialized. Call initialize() first.');
    }
    return this.db;
  }

  // Watchlist operations
  async saveWatchlist(id: string, data: any): Promise<void> {
    const db = this.ensureDB();
    await db.put('watchlists', {
      id,
      data,
      timestamp: Date.now(),
    });
  }

  async getWatchlist(id: string): Promise<any | null> {
    const db = this.ensureDB();
    const result = await db.get('watchlists', id);
    return result?.data || null;
  }

  async getAllWatchlists(): Promise<any[]> {
    const db = this.ensureDB();
    const results = await db.getAll('watchlists');
    return results.map(r => r.data);
  }

  async deleteWatchlist(id: string): Promise<void> {
    const db = this.ensureDB();
    await db.delete('watchlists', id);
  }

  // Movie operations
  async saveMovie(id: string, data: any): Promise<void> {
    const db = this.ensureDB();
    await db.put('movies', {
      id,
      data,
      timestamp: Date.now(),
    });
  }

  async getMovie(id: string): Promise<any | null> {
    const db = this.ensureDB();
    const result = await db.get('movies', id);
    return result?.data || null;
  }

  async getAllMovies(): Promise<any[]> {
    const db = this.ensureDB();
    const results = await db.getAll('movies');
    return results.map(r => r.data);
  }

  // User movie data operations
  async saveUserMovieData(id: string, data: any): Promise<void> {
    const db = this.ensureDB();
    await db.put('userMovieData', {
      id,
      data,
      timestamp: Date.now(),
    });
  }

  async getUserMovieData(id: string): Promise<any | null> {
    const db = this.ensureDB();
    const result = await db.get('userMovieData', id);
    return result?.data || null;
  }

  async getAllUserMovieData(): Promise<any[]> {
    const db = this.ensureDB();
    const results = await db.getAll('userMovieData');
    return results.map(r => r.data);
  }

  // Pending actions queue (for offline writes)
  async addPendingAction(
    type: 'add' | 'update' | 'delete',
    collection: string,
    data: any
  ): Promise<string> {
    const db = this.ensureDB();
    const id = `${collection}_${Date.now()}_${Math.random()}`;

    await db.add('pendingActions', {
      id,
      type,
      collection,
      data,
      timestamp: Date.now(),
      retryCount: 0,
    });

    console.log(`📝 OfflineStorage: Added pending action ${type} for ${collection}`);
    return id;
  }

  async getPendingActions(): Promise<any[]> {
    const db = this.ensureDB();
    return await db.getAll('pendingActions');
  }

  async deletePendingAction(id: string): Promise<void> {
    const db = this.ensureDB();
    await db.delete('pendingActions', id);
  }

  async updatePendingActionRetryCount(id: string, retryCount: number): Promise<void> {
    const db = this.ensureDB();
    const action = await db.get('pendingActions', id);
    if (action) {
      action.retryCount = retryCount;
      await db.put('pendingActions', action);
    }
  }

  async clearPendingActions(): Promise<void> {
    const db = this.ensureDB();
    const tx = db.transaction('pendingActions', 'readwrite');
    await tx.store.clear();
    await tx.done;
  }

  // App metadata operations
  async setMetadata(key: string, value: any): Promise<void> {
    const db = this.ensureDB();
    await db.put('appMetadata', value, key);
  }

  async getMetadata(key: string): Promise<any> {
    const db = this.ensureDB();
    return await db.get('appMetadata', key);
  }

  // Cleanup old data
  async cleanupOldData(maxAgeMs: number = 30 * 24 * 60 * 60 * 1000): Promise<void> {
    const db = this.ensureDB();
    const cutoffTime = Date.now() - maxAgeMs;

    const stores = ['watchlists', 'movies', 'userMovieData'] as const;

    for (const storeName of stores) {
      const tx = db.transaction(storeName, 'readwrite');
      const index = tx.store.index('by-timestamp');
      const oldItems = await index.getAll(IDBKeyRange.upperBound(cutoffTime));

      for (const item of oldItems) {
        await tx.store.delete(item.id);
      }

      await tx.done;
    }

    console.log(`🧹 OfflineStorage: Cleaned up data older than ${maxAgeMs}ms`);
  }

  // Clear all data
  async clearAllData(): Promise<void> {
    const db = this.ensureDB();
    const stores = ['watchlists', 'movies', 'userMovieData', 'pendingActions', 'appMetadata'];

    for (const storeName of stores) {
      const tx = db.transaction(storeName as any, 'readwrite');
      await tx.store.clear();
      await tx.done;
    }

    console.log('🧹 OfflineStorage: All data cleared');
  }

  // Get storage statistics
  async getStorageStats(): Promise<{
    watchlists: number;
    movies: number;
    userMovieData: number;
    pendingActions: number;
  }> {
    const db = this.ensureDB();

    const [watchlists, movies, userMovieData, pendingActions] = await Promise.all([
      db.count('watchlists'),
      db.count('movies'),
      db.count('userMovieData'),
      db.count('pendingActions'),
    ]);

    return {
      watchlists,
      movies,
      userMovieData,
      pendingActions,
    };
  }
}

// Export singleton instance
export const offlineStorage = new OfflineStorageService();
